import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext
} from 'react';
import _get from 'lodash/get';

import { Container, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import SimpleDivider from '../../../../../Components/Dividers/SimpleDivider';
import ModalProgress from '../../../../../Components/Progress/Modal/ModalProgress';
import BaseButton from '../../../../../Components/Buttons/BaseButton';
import BaseDialog from '../../../../../Components/Dialogs/BaseDialog';

import { AlertsDispatchContext } from '../../../../../Contexts/AlertsContext';

import IndividualLoad from '../LoadTypes/IndividualLoad/IndividualLoad';
import MultipleLoad from '../LoadTypes/MultipleLoad/MultipleLoad';
import PaymentAccordion from '../CustomComponents/PaymentAccordion';
import SimpleTabs from '../../../Tabs/CustomTabs';
import {
  getTotalAmount,
  isPaymentOptionDisabled,
  normalizePaymentType
} from '../../../helpers/helpers';
import {
  INDIVIDUAL_LOAD_INITIAL_STATE,
  MULTIPLE_LOAD_INITIAL_STATE,
  PAYMENT_STEPS,
  SELECT_OPTIONS,
  TYPE_OF_PAYMENTS
} from '../../../payment_enums';

// * Utils
import { moneyWithDots } from '../../../../../Utils/Format/MoneyFormat';
import { COUPON_VALUES } from '../../../../../Utils/enums';
import { Company } from '../../../../../Configs/general';
import { CurrentStepIndexContext } from '../../../../../Contexts/StepperContext';
import { useCaptcha } from '../../../../../Hooks/useCaptcha';
import { TAB_PROPS, UPLOAD_MODE } from './MultiplePayment';
import SweetAlert from '../../../../../Components/Alerts/SweetAlert';
import { ALERT_TYPE } from '../../../../../Components/Alerts/alert_enums';

const MultiplePaymentGDC = props => {
  const {
    formId,
    number,
    setNumber,
    type,
    setType,
    payments,
    setPayments,
    cardIndividualLoad,
    setCardIndividualLoad,
    cardMultipleLoad,
    setCardMultipleLoad,
    totalIndividualCards,
    totalMultipleLoadCards,
    totalCurrentCards,
    currentTabIndex,
    setCurrentTabIndex,
    authToken
  } = props;

  const setAlert = useContext(AlertsDispatchContext);
  const currentStepIndex = useContext(CurrentStepIndexContext);

  // * STATE HOOKS;
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firstAccordionExpanded, setFirstAccordionExpanded] = useState(false);
  const [secondAccordionExpanded, setSecondAccordionExpanded] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const {
    captchaRef: multipleCaptchaRef,
    handlers: multipleCaptchaHandlers,
    captchaToken: multipleCaptchaToken,
    loading: multipleCaptchaLoading
  } = useCaptcha();
  const {
    captchaRef: individualCaptchaRef,
    handlers: individualCaptchaHandlers,
    captchaToken: individualCaptchaToken,
    loading: individualCaptchaLoading
  } = useCaptcha();

  const inputRef = useRef(null);
  const inputMultipleLoadRef = useRef(null);

  const canSubmitIndividual =
    !loading && individualCaptchaToken && number && type;
  const contractPaymentOption = SELECT_OPTIONS.find(
    option => option.value === TYPE_OF_PAYMENTS.contract
  );
  const couponPaymentOption = SELECT_OPTIONS.find(
    option => option.value === TYPE_OF_PAYMENTS.coupon
  );

  const isContractPaymentDisabled =
    contractPaymentOption && isPaymentOptionDisabled(contractPaymentOption);
  const isCouponPaymentDisabled =
    couponPaymentOption && isPaymentOptionDisabled(couponPaymentOption);

  const canSubmitMultiple =
    !loading && multipleCaptchaToken && !isContractPaymentDisabled;
  const isIndividualTab = currentTabIndex.checked === UPLOAD_MODE.INDIVIDUAL;
  const loadingCaptcha = isIndividualTab
    ? individualCaptchaLoading
    : multipleCaptchaLoading;

  const hasDisabledPaymentOption =
    isContractPaymentDisabled || isCouponPaymentDisabled;

  const hasEnabledPaymentOption = SELECT_OPTIONS.some(
    option => !isPaymentOptionDisabled(option)
  );

  let disabledPaymentOptionMessage = '';

  if (!hasEnabledPaymentOption) {
    disabledPaymentOptionMessage =
      'En este momento no hay referencias de pago disponibles debido a labores de mantenimiento en nuestra plataforma. Por favor intenta nuevamente más tarde.';
  } else if (isContractPaymentDisabled) {
    disabledPaymentOptionMessage = `Por mantenimiento en la referencia por ${Company.contractConjugation.regular.singular.main}, la opción de carga masiva está desactivada temporalmente. Puede continuar el proceso con su cupón de referencia en Carga individual.`;
  } else {
    disabledPaymentOptionMessage = `La opción de pago por cupón no está disponible temporalmente debido a labores de mantenimiento en nuestra plataforma. Puede continuar el proceso con su referencia por ${Company.contractConjugation.regular.singular.main}.`;
  }

  // * OTHER HOOKS
  const classes = useStyles();

  useEffect(() => {
    const nextType = normalizePaymentType(SELECT_OPTIONS, type);

    if (nextType !== type) {
      setType(nextType);
    }
  }, [type, setType]);

  useEffect(() => {
    const totalLoadingIndividual = cardIndividualLoad.loading.length;
    const totalLoadingMultiple = cardMultipleLoad.loading.length;
    const validTransactions =
      totalLoadingIndividual + totalLoadingMultiple === 0 &&
      (cardIndividualLoad.toPay.length > 0 ||
        cardMultipleLoad.toPay.length > 0);

    const formIsEmpty = loading || !validTransactions || !type;

    if (formIsEmpty) {
      props.setDisabledNext(true);
    } else {
      // PayU does not allow us to pay coupons with such a small value (1 COP),
      // so disable the Next button when that happens.
      let canPay = false;
      if (payments.length === 1) {
        const value =
          _get(payments[0], 'detail.couponValue') ||
          _get(payments[0], 'detail.value');
        canPay = value >= COUPON_VALUES.MIN_VALUE;
      } else {
        const totalValue = getTotalAmount([
          ...cardIndividualLoad.toPay,
          ...cardMultipleLoad.toPay
        ]);
        canPay = totalValue >= COUPON_VALUES.MIN_VALUE;
      }
      props.setDisabledNext(!canPay);
      if (!canPay) {
        setAlert({
          type: 'info',
          message: `Solo se permite realizar pagos mayores a ${COUPON_VALUES.MIN_VALUE} peso.`
        });
      }
    }
  }, [
    type,
    number,
    loading,
    payments,
    props,
    setAlert,
    cardIndividualLoad,
    cardMultipleLoad
  ]);

  const changeTabAndRemove = () => {
    setCardIndividualLoad(INDIVIDUAL_LOAD_INITIAL_STATE);
    setCardMultipleLoad(MULTIPLE_LOAD_INITIAL_STATE);
    setPayments([]);
    setCurrentTabIndex(prev => ({ ...prev, checked: prev.noChecked }));
    setOpenDialog(false);
  };

  const handleTabChange = (_event, newValue) => {
    if (totalIndividualCards.length > 0 || totalMultipleLoadCards.length > 0) {
      setOpenDialog(true);
      setCurrentTabIndex(prev => ({ ...prev, noChecked: newValue }));
      return;
    }
    setCurrentTabIndex({ noChecked: newValue, checked: newValue });
  };

  const renderDialogActions = () => {
    return (
      <>
        <BaseButton onClick={() => changeTabAndRemove()}>Entendido</BaseButton>
      </>
    );
  };

  const renderDialogContent = useCallback(() => {
    return (
      <>
        <Typography className={classes.dialogText}>
          Al cambiar de opción,{' '}
          {Company.contractConjugation.regular.plural.article} y/o cupones que
          haya agregado serán removidos
        </Typography>
      </>
    );
  }, [classes.dialogText]);

  return (
    <>
      {loadingCaptcha && (
        <ModalProgress
          id="QueryPayment_progress_loadingCaptcha"
          message="Cargando"
        />
      )}
      <Container
        maxWidth="md"
        className={`${classes.root} ${
          loadingCaptcha ? classes.captchaLoading : ''
        }`}
      >
        {loading && (
          <ModalProgress
            id="QueryPayment_progress_loading"
            message="Buscando"
          />
        )}
        {openDialog && (
          <BaseDialog
            handleClose={() => setOpenDialog(false)}
            open={openDialog}
            title="Estimado cliente"
            actions={renderDialogActions}
            content={renderDialogContent}
            contentStyle={classes.dialogContent}
            contentSize="small"
          />
        )}
        <Typography className={classes.subtitle}>
          {PAYMENT_STEPS[currentStepIndex].subtitle}
        </Typography>
        {hasDisabledPaymentOption && (
          <Grid item xs={12} className={classes.paymentReferenceSweetAlert}>
            <SweetAlert
              type={ALERT_TYPE.WARNING}
              message={disabledPaymentOptionMessage}
            />
          </Grid>
        )}
        <SimpleTabs
          tabProps={TAB_PROPS}
          currentTabIndex={currentTabIndex.checked}
          handleChange={handleTabChange}
        >
          <MultipleLoad
            formId={formId}
            recaptchaRef={multipleCaptchaRef}
            captcha={multipleCaptchaToken}
            captchaHandlers={multipleCaptchaHandlers}
            canSubmit={canSubmitMultiple}
            inputRef={inputMultipleLoadRef}
            loadingCaptcha={multipleCaptchaLoading}
            cardMultipleLoad={cardMultipleLoad}
            setCardMultipleLoad={setCardMultipleLoad}
            totalCurrentCards={totalCurrentCards}
            setFirstAccordionExpanded={setFirstAccordionExpanded}
            setSecondAccordionExpanded={setSecondAccordionExpanded}
            setCaptchaToken={setCaptchaToken}
            setPayments={setPayments}
            payments={payments}
            authToken={authToken}
          />
          <IndividualLoad
            formId={formId}
            setDisabledNext={props.setDisabledNext}
            number={number}
            type={type}
            setNumber={setNumber}
            setType={setType}
            setPayments={setPayments}
            recaptchaRef={individualCaptchaRef}
            inputRef={inputRef}
            captchaHandlers={individualCaptchaHandlers}
            loadingCaptcha={individualCaptchaLoading}
            canSubmit={canSubmitIndividual}
            setLoading={setLoading}
            loading={loading}
            cardIndividualLoad={cardIndividualLoad}
            setCardIndividualLoad={setCardIndividualLoad}
            totalCurrentCards={totalCurrentCards}
            setFirstAccordionExpanded={setFirstAccordionExpanded}
            setSecondAccordionExpanded={setSecondAccordionExpanded}
            captcha={individualCaptchaToken}
            setCaptchaToken={setCaptchaToken}
          />
        </SimpleTabs>
        <PaymentAccordion
          containerClass={classes.accordionContainer}
          totalIndividualCards={totalIndividualCards}
          totalMultipleLoadCards={totalMultipleLoadCards}
          setCardIndividualLoad={setCardIndividualLoad}
          cardMultipleLoad={cardMultipleLoad}
          setCardMultipleLoad={setCardMultipleLoad}
          number={number}
          payments={payments}
          setPayments={setPayments}
          firstAccordionExpanded={firstAccordionExpanded}
          setFirstAccordionExpanded={setFirstAccordionExpanded}
          secondAccordionExpanded={secondAccordionExpanded}
          setSecondAccordionExpanded={setSecondAccordionExpanded}
          captchaValue={captchaToken}
          currentTabIndex={currentTabIndex.checked}
          authToken={authToken}
        />
        <SimpleDivider withoutMargin />
        <Box className={classes.amountContainer}>
          <Typography className={classes.totalAmountLabel}>
            Total a pagar
          </Typography>
          <Typography className={classes.totalAmountText}>
            {moneyWithDots(
              getTotalAmount([
                ...cardIndividualLoad.toPay,
                ...cardMultipleLoad.toPay
              ])
            )}
          </Typography>
        </Box>
      </Container>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-around',
    padding: 0,
    // * Responsive
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0)
    },
    [theme.breakpoints.up('xs')]: {
      marginTop: theme.spacing(),
      marginBottom: theme.spacing()
    }
  },
  title: {
    fontWeight: 600,
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: 14,
      padding: theme.spacing(0, 2)
    }
  },
  subtitle: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.color.default,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: 11,
      padding: theme.spacing(0, 2),
      marginBottom: theme.spacing(2)
    }
  },
  captchaLoading: {
    visibility: 'hidden'
  },
  amountContainer: {
    padding: theme.spacing(0, 2),
    marginBottom: 200
  },
  totalAmountLabel: {
    color: theme.palette.color.default,
    fontSize: 14,
    fontWeight: 500,
    marginTop: theme.spacing(3),
    textAlign: 'right'
  },
  totalAmountText: {
    fontSize: 24,
    fontWeight: 600,
    textAlign: 'right'
  },
  accordionContainer: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2)
    }
  },
  dialogContent: {
    minHeight: 'unset'
  },
  dialogText: {
    fontSize: 14
  },
  paymentReferenceSweetAlert: {
    margin: theme.spacing(1, 0, 2)
  }
}));

export default MultiplePaymentGDC;
